/**
 * 全局状态管理 — Zustand
 * 使用 persist 中间件将设置自动持久化到 localStorage
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_APP_NAME, resolveAppName } from '../../common/appBrand';
import { DEFAULT_DOUYIN_CREATOR_TEST_CART_LINK } from '../../common/enums/douyinCreatorDebug';
import type { EcommerceAccountDetail, InsightCategoryNode, InsightCategoryOption } from '../api';
import type { DataSpace, DemoWorkspace } from '../api/demoWorkspace';
import type { EcommerceShopContext } from '../api/ecommerceAccounts';
import type { DouyinImTaskQueueMap, DouyinImTaskQueueItem } from './douyinImTaskQueueState';
import { clearProductCategoriesCaches } from './productCategories';

// ---------- 应用设置 ----------
interface AppSettings {
  notifications: boolean;
  autoUpdate: boolean;
  apiKey: string;
}

interface AuthUser {
  id: string;
  nickname?: string;
  avatar?: string;
  phone?: string;
  isAdmin?: boolean;
  invitationCode?: string;
}

interface AuthState {
  userId: string;       // 非空字符串即表示已登录（Cookie Session 模式下不再存 token）
  user: AuthUser | null;
  loginStatus: 'idle' | 'loading' | 'authenticated' | 'error';
  lastError: string;
}

interface AppConfigState {
  appName: string;
}

interface InsightCategoriesState {
  options: InsightCategoryNode[];
  loading: boolean;
  lastError: string;
  loadedAt: number;
}

interface ProductCategoriesState {
  shopId: string;
  options: InsightCategoryOption[];
  loading: boolean;
  lastError: string;
  loadedAt: number;
  status?: string;
  confirmed?: boolean;
  recommendedOptions?: InsightCategoryOption[];
  maxCount?: number;
}

interface CurrentEcomAccountDetailState {
  accountId: string;
  data: EcommerceAccountDetail | null;
  loading: boolean;
  lastError: string;
  loadedAt: number;
}

interface DataSpaceState {
  dataSpace: DataSpace;
  demoWorkspace: DemoWorkspace | null;
  previousUserShopId: string;
}

interface DouyinImDebugState {
  enabled: boolean;
  executionOverlayEnabled: boolean;
  creatorTestBrowserStateEnabled: boolean;
  creatorTestCartLinkEnabled: boolean;
  creatorTestCartLink: string;
}

interface DisplayWorkbenchState {
  dashboardShowcaseEnabled: boolean;
  devLauncherHidden: boolean;
  templateSyncEnabled: boolean;
  storyboardEnabled: boolean;
}

export type DeviceCaptureMode = 'single' | 'low' | 'high' | 'stream' | 'stream-loading';

const EMPTY_CURRENT_ECOM_ACCOUNT_DETAIL_STATE: CurrentEcomAccountDetailState = {
  accountId: '',
  data: null,
  loading: false,
  lastError: '',
  loadedAt: 0,
};

const DEFAULT_DOUYIN_IM_DEBUG_STATE: DouyinImDebugState = {
  enabled: false,
  executionOverlayEnabled: true,
  creatorTestBrowserStateEnabled: false,
  creatorTestCartLinkEnabled: false,
  creatorTestCartLink: DEFAULT_DOUYIN_CREATOR_TEST_CART_LINK,
};

const DEFAULT_DISPLAY_WORKBENCH_STATE: DisplayWorkbenchState = {
  dashboardShowcaseEnabled: false,
  devLauncherHidden: false,
  templateSyncEnabled: false,
  storyboardEnabled: false,
};

const EMPTY_DOUYIN_IM_TASK_QUEUE: DouyinImTaskQueueMap = {};

// ---------- 全局状态定义 ----------
interface AppState {
  appConfig: AppConfigState;
  setAppName: (appName: string) => void;

  // 主题
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;
  toggleTheme: () => void;

  // 设置
  settings: AppSettings;
  updateSettings: (patch: Partial<AppSettings>) => void;

  // 登录态
  auth: AuthState;
  setAuthLoading: () => void;
  setAuthSession: (payload: { userId: string; user: AuthUser | null }) => void;
  setAuthError: (message: string) => void;
  clearAuthSession: () => void;

  // 全局类目池
  insightCategories: InsightCategoriesState;
  setInsightCategoriesLoading: (loading: boolean) => void;
  setInsightCategoriesOptions: (options: InsightCategoryNode[]) => void;
  setInsightCategoriesError: (message: string) => void;

  // 当前系统账号下的关注类目
  productCategories: ProductCategoriesState;
  setProductCategoriesLoading: (shopId: string, loading: boolean) => void;
  setProductCategoriesOptions: (shopId: string, options: InsightCategoryOption[]) => void;
  setProductCategoriesError: (shopId: string, message: string) => void;
  shopOperatingCategories: ProductCategoriesState;
  setShopOperatingCategoriesLoading: (shopId: string, loading: boolean) => void;
  setShopOperatingCategoriesOptions: (payload: {
    shopId: string;
    options: InsightCategoryOption[];
    status?: string;
    confirmed?: boolean;
    recommendedOptions?: InsightCategoryOption[];
    maxCount?: number;
  }) => void;
  setShopOperatingCategoriesError: (shopId: string, message: string) => void;

  // 店铺上下文列表（全局共享，Settings 解绑/绑定后同步刷新侧边栏）
  shopContexts: EcommerceShopContext[];
  shopContextsLoading: boolean;
  setShopContexts: (contexts: EcommerceShopContext[]) => void;
  setShopContextsLoading: (loading: boolean) => void;

  currentShopId: string;
  setCurrentShopId: (shopId: string) => void;
  dataSpaceState: DataSpaceState;
  enterDemoWorkspace: (workspace: DemoWorkspace) => void;
  exitDemoWorkspace: () => void;
  // 当前使用的带货账号
  currentEcomAccountId: string;
  setCurrentEcomAccountId: (ecomAccountId: string) => void;
  currentEcomAccountDetail: CurrentEcomAccountDetailState;
  setCurrentEcomAccountDetailLoading: (accountId: string, loading: boolean) => void;
  setCurrentEcomAccountDetail: (detail: EcommerceAccountDetail) => void;
  setCurrentEcomAccountDetailError: (accountId: string, message: string) => void;
  clearCurrentEcomAccountDetail: () => void;

  // 抖音私信浏览器调试开关
  douyinImDebug: DouyinImDebugState;
  setDouyinImDebugEnabled: (enabled: boolean) => void;
  setDouyinExecutionOverlayEnabled: (enabled: boolean) => void;
  setDouyinCreatorTestBrowserStateEnabled: (enabled: boolean) => void;
  setDouyinCreatorTestCartLinkEnabled: (enabled: boolean) => void;
  setDouyinCreatorTestCartLink: (cartLink: string) => void;

  // 内部展示调试开关，仅影响本机首页展示层
  displayWorkbench: DisplayWorkbenchState;
  setDashboardShowcaseEnabled: (enabled: boolean) => void;
  setDevLauncherHidden: (hidden: boolean) => void;
  setTemplateSyncEnabled: (enabled: boolean) => void;
  setStoryboardEnabled: (enabled: boolean) => void;

  // 抖音私信本地任务队列
  douyinImTaskQueue: DouyinImTaskQueueMap;
  upsertDouyinImTask: (task: DouyinImTaskQueueItem) => void;
  removeDouyinImTask: (taskKey: string) => void;
  replaceDouyinImTaskQueue: (tasks: DouyinImTaskQueueMap) => void;

  // 侧边栏
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // 我的设备页会话态：路由切换保留，刷新应用后重置
  deviceCaptureModes: Record<string, DeviceCaptureMode>;
  setDeviceCaptureMode: (clientId: string, mode: DeviceCaptureMode) => void;
  replaceDeviceCaptureModes: (modes: Record<string, DeviceCaptureMode>) => void;

}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // 主题
      appConfig: {
        appName: DEFAULT_APP_NAME,
      },
      setAppName: (appName) =>
        set(() => ({
          appConfig: {
            appName: resolveAppName(appName),
          },
        })),

      // 主题
      theme: 'light',
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set((s) => ({ theme: s.theme === 'light' ? 'dark' : 'light' })),

      // 设置
      settings: {
        notifications: true,
        autoUpdate: true,
        apiKey: '',
      },
      updateSettings: (patch) =>
        set((s) => ({ settings: { ...s.settings, ...patch } })),

      // 登录态
      auth: {
        userId: '',
        user: null,
        loginStatus: 'idle',
        lastError: '',
      },
      setAuthLoading: () =>
        set((s) => ({
          auth: {
            ...s.auth,
            loginStatus: 'loading',
            lastError: '',
          },
        })),
      setAuthSession: ({ userId, user }) =>
        set(() => ({
          auth: {
            userId,
            user,
            loginStatus: 'authenticated',
            lastError: '',
          },
        })),
      setAuthError: (message) =>
        set((s) => ({
          auth: {
            ...s.auth,
            loginStatus: 'error',
            lastError: message,
          },
        })),
      clearAuthSession: () => {
        clearProductCategoriesCaches();
        set(() => ({
          auth: {
            userId: '',
            user: null,
            loginStatus: 'idle',
            lastError: '',
          },
          insightCategories: {
            options: [],
            loading: false,
            lastError: '',
            loadedAt: 0,
          },
          productCategories: {
            shopId: '',
            options: [],
            loading: false,
            lastError: '',
            loadedAt: 0,
          },
          shopOperatingCategories: {
            shopId: '',
            options: [],
            loading: false,
            lastError: '',
            loadedAt: 0,
            status: '',
            confirmed: false,
            recommendedOptions: [],
            maxCount: 5,
          },
          shopContexts: [],
          shopContextsLoading: false,
          currentShopId: '',
          currentEcomAccountId: '',
          currentEcomAccountDetail: EMPTY_CURRENT_ECOM_ACCOUNT_DETAIL_STATE,
          dataSpaceState: {
            dataSpace: 'USER' as DataSpace,
            demoWorkspace: null,
            previousUserShopId: '',
          },
          douyinImTaskQueue: EMPTY_DOUYIN_IM_TASK_QUEUE,
        }));
      },

      // 全局类目池
      insightCategories: {
        options: [],
        loading: false,
        lastError: '',
        loadedAt: 0,
      },
      setInsightCategoriesLoading: (loading) =>
        set((s) => ({
          insightCategories: {
            ...s.insightCategories,
            loading,
            lastError: loading ? '' : s.insightCategories.lastError,
          },
        })),
      setInsightCategoriesOptions: (options) =>
        set(() => ({
          insightCategories: {
            options,
            loading: false,
            lastError: '',
            loadedAt: Date.now(),
          },
        })),
      setInsightCategoriesError: (message) =>
        set((s) => ({
          insightCategories: {
            ...s.insightCategories,
            loading: false,
            lastError: message,
          },
        })),

      productCategories: {
        shopId: '',
        options: [],
        loading: false,
        lastError: '',
        loadedAt: 0,
      },
      setProductCategoriesLoading: (shopId, loading) =>
        set((s) => ({
          productCategories: {
            shopId,
            options: shopId === s.productCategories.shopId ? s.productCategories.options : [],
            loading,
            lastError: loading ? '' : s.productCategories.lastError,
            loadedAt: shopId === s.productCategories.shopId ? s.productCategories.loadedAt : 0,
          },
        })),
      setProductCategoriesOptions: (shopId, options) =>
        set(() => ({
          productCategories: {
            shopId,
            options,
            loading: false,
            lastError: '',
            loadedAt: Date.now(),
          },
        })),
      setProductCategoriesError: (shopId, message) =>
        set((s) => ({
          productCategories: {
            shopId,
            options: shopId === s.productCategories.shopId ? s.productCategories.options : [],
            loading: false,
            lastError: message,
            loadedAt: shopId === s.productCategories.shopId ? s.productCategories.loadedAt : 0,
          },
        })),

      shopOperatingCategories: {
        shopId: '',
        options: [],
        loading: false,
        lastError: '',
        loadedAt: 0,
        status: '',
        confirmed: false,
        recommendedOptions: [],
        maxCount: 5,
      },
      setShopOperatingCategoriesLoading: (shopId, loading) =>
        set((s) => {
          const isSameShop = shopId === s.shopOperatingCategories.shopId;
          return {
            shopOperatingCategories: {
              ...s.shopOperatingCategories,
              shopId,
              options: isSameShop ? s.shopOperatingCategories.options : [],
              recommendedOptions: isSameShop ? s.shopOperatingCategories.recommendedOptions : [],
              loading,
              lastError: loading ? '' : s.shopOperatingCategories.lastError,
              loadedAt: isSameShop ? s.shopOperatingCategories.loadedAt : 0,
            },
          };
        }),
      setShopOperatingCategoriesOptions: (payload) =>
        set(() => ({
          shopOperatingCategories: {
            shopId: payload.shopId,
            options: payload.options,
            loading: false,
            lastError: '',
            loadedAt: Date.now(),
            status: payload.status,
            confirmed: payload.confirmed,
            recommendedOptions: payload.recommendedOptions ?? [],
            maxCount: payload.maxCount ?? 5,
          },
        })),
      setShopOperatingCategoriesError: (shopId, message) =>
        set((s) => ({
          shopOperatingCategories: {
            ...s.shopOperatingCategories,
            shopId,
            options: shopId === s.shopOperatingCategories.shopId ? s.shopOperatingCategories.options : [],
            loading: false,
            lastError: message,
            loadedAt: shopId === s.shopOperatingCategories.shopId ? s.shopOperatingCategories.loadedAt : 0,
          },
        })),

      shopContexts: [],
      shopContextsLoading: false,
      setShopContexts: (shopContexts) => set({ shopContexts }),
      setShopContextsLoading: (shopContextsLoading) => set({ shopContextsLoading }),

      currentShopId: '',
      setCurrentShopId: (currentShopId) =>
        set((state) => ({
          currentShopId,
          currentEcomAccountId: currentShopId === state.currentShopId ? state.currentEcomAccountId : '',
          currentEcomAccountDetail: currentShopId === state.currentShopId
            ? state.currentEcomAccountDetail
            : EMPTY_CURRENT_ECOM_ACCOUNT_DETAIL_STATE,
        })),
      dataSpaceState: {
        dataSpace: 'USER',
        demoWorkspace: null,
        previousUserShopId: '',
      },
      enterDemoWorkspace: (workspace) =>
        set((state) => {
          const demoShopId = workspace.shop?.ecom_account_id || workspace.shop?.shop_id || '';
          return {
            dataSpaceState: {
              dataSpace: 'DEMO',
              demoWorkspace: workspace,
              previousUserShopId: state.dataSpaceState.dataSpace === 'DEMO'
                ? state.dataSpaceState.previousUserShopId
                : state.currentShopId,
            },
            currentShopId: demoShopId,
            currentEcomAccountId: '',
            currentEcomAccountDetail: EMPTY_CURRENT_ECOM_ACCOUNT_DETAIL_STATE,
            productCategories: {
              ...state.productCategories,
              shopId: '',
              options: [],
              loadedAt: 0,
            },
            shopOperatingCategories: {
              ...state.shopOperatingCategories,
              shopId: '',
              options: [],
              loadedAt: 0,
            },
          };
        }),
      exitDemoWorkspace: () =>
        set((state) => ({
          dataSpaceState: {
            dataSpace: 'USER',
            demoWorkspace: null,
            previousUserShopId: '',
          },
          currentShopId: state.dataSpaceState.previousUserShopId,
          currentEcomAccountId: '',
          currentEcomAccountDetail: EMPTY_CURRENT_ECOM_ACCOUNT_DETAIL_STATE,
          productCategories: {
            ...state.productCategories,
            shopId: '',
            options: [],
            loadedAt: 0,
          },
          shopOperatingCategories: {
            ...state.shopOperatingCategories,
            shopId: '',
            options: [],
            loadedAt: 0,
          },
        })),
      currentEcomAccountId: '',
      setCurrentEcomAccountId: (currentEcomAccountId) =>
        set((state) => {
          const shouldResetDetail = state.currentEcomAccountId !== currentEcomAccountId;
          return {
            currentEcomAccountId,
            currentEcomAccountDetail: shouldResetDetail
              ? EMPTY_CURRENT_ECOM_ACCOUNT_DETAIL_STATE
              : state.currentEcomAccountDetail,
          };
        }),
      currentEcomAccountDetail: EMPTY_CURRENT_ECOM_ACCOUNT_DETAIL_STATE,
      setCurrentEcomAccountDetailLoading: (accountId, loading) =>
        set((state) => ({
          currentEcomAccountDetail: {
            accountId,
            data: state.currentEcomAccountDetail.accountId === accountId ? state.currentEcomAccountDetail.data : null,
            loading,
            lastError: loading ? '' : state.currentEcomAccountDetail.lastError,
            loadedAt: state.currentEcomAccountDetail.loadedAt,
          },
        })),
      setCurrentEcomAccountDetail: (detail) =>
        set(() => ({
          currentEcomAccountDetail: {
            accountId: detail.ecom_account_id,
            data: detail,
            loading: false,
            lastError: '',
            loadedAt: Date.now(),
          },
        })),
      setCurrentEcomAccountDetailError: (accountId, message) =>
        set((state) => ({
          currentEcomAccountDetail: {
            accountId,
            data: state.currentEcomAccountDetail.accountId === accountId ? state.currentEcomAccountDetail.data : null,
            loading: false,
            lastError: message,
            loadedAt: state.currentEcomAccountDetail.loadedAt,
          },
        })),
      clearCurrentEcomAccountDetail: () =>
        set(() => ({
          currentEcomAccountDetail: EMPTY_CURRENT_ECOM_ACCOUNT_DETAIL_STATE,
        })),

      douyinImDebug: DEFAULT_DOUYIN_IM_DEBUG_STATE,
      setDouyinImDebugEnabled: (enabled) =>
        set((state) => ({
          douyinImDebug: {
            ...state.douyinImDebug,
            enabled,
          },
        })),
      setDouyinExecutionOverlayEnabled: (executionOverlayEnabled) =>
        set((state) => ({
          douyinImDebug: {
            ...state.douyinImDebug,
            executionOverlayEnabled,
          },
        })),
      setDouyinCreatorTestBrowserStateEnabled: (creatorTestBrowserStateEnabled) =>
        set((state) => ({
          douyinImDebug: {
            ...state.douyinImDebug,
            creatorTestBrowserStateEnabled,
          },
        })),
      setDouyinCreatorTestCartLinkEnabled: (creatorTestCartLinkEnabled) =>
        set((state) => ({
          douyinImDebug: {
            ...state.douyinImDebug,
            creatorTestCartLinkEnabled,
          },
        })),
      setDouyinCreatorTestCartLink: (creatorTestCartLink) =>
        set((state) => ({
          douyinImDebug: {
            ...state.douyinImDebug,
            creatorTestCartLink,
          },
        })),

      displayWorkbench: DEFAULT_DISPLAY_WORKBENCH_STATE,
      setDashboardShowcaseEnabled: (dashboardShowcaseEnabled) =>
        set((state) => ({
          displayWorkbench: {
            ...state.displayWorkbench,
            dashboardShowcaseEnabled,
          },
        })),
      setDevLauncherHidden: (devLauncherHidden) =>
        set((state) => ({
          displayWorkbench: {
            ...state.displayWorkbench,
            devLauncherHidden,
          },
        })),
      setTemplateSyncEnabled: (templateSyncEnabled) =>
        set((state) => ({
          displayWorkbench: {
            ...state.displayWorkbench,
            templateSyncEnabled,
          },
        })),
      setStoryboardEnabled: (storyboardEnabled) =>
        set((state) => ({
          displayWorkbench: {
            ...state.displayWorkbench,
            storyboardEnabled,
          },
        })),

      douyinImTaskQueue: EMPTY_DOUYIN_IM_TASK_QUEUE,
      upsertDouyinImTask: (task) =>
        set((state) => ({
          douyinImTaskQueue: {
            ...state.douyinImTaskQueue,
            [task.taskKey]: task,
          },
        })),
      removeDouyinImTask: (taskKey) =>
        set((state) => {
          if (!state.douyinImTaskQueue[taskKey]) {
            return state;
          }

          const nextQueue = { ...state.douyinImTaskQueue };
          delete nextQueue[taskKey];
          return {
            douyinImTaskQueue: nextQueue,
          };
        }),
      replaceDouyinImTaskQueue: (tasks) =>
        set(() => ({
          douyinImTaskQueue: tasks,
        })),

      // 侧边栏
      sidebarCollapsed: false,
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      deviceCaptureModes: {},
      setDeviceCaptureMode: (clientId, mode) =>
        set((state) => {
          if (state.deviceCaptureModes[clientId] === mode) {
            return state;
          }

          return {
            deviceCaptureModes: {
              ...state.deviceCaptureModes,
              [clientId]: mode,
            },
          };
        }),
      replaceDeviceCaptureModes: (deviceCaptureModes) => set({ deviceCaptureModes }),
    }),
    {
      name: 'fluxmind-app-settings', // localStorage key
      version: 5,
      merge: (persistedState, currentState) => {
        const persisted = persistedState && typeof persistedState === 'object' && !Array.isArray(persistedState)
          ? persistedState as Partial<AppState>
          : {};

        return {
          ...currentState,
          ...persisted,
          douyinImDebug: {
            ...DEFAULT_DOUYIN_IM_DEBUG_STATE,
            ...persisted.douyinImDebug,
          },
          displayWorkbench: {
            ...DEFAULT_DISPLAY_WORKBENCH_STATE,
            ...persisted.displayWorkbench,
          },
          dataSpaceState: {
            dataSpace: 'USER',
            demoWorkspace: null,
            previousUserShopId: '',
            ...persisted.dataSpaceState,
          },
        };
      },
      migrate: (persistedState) => {
        if (!persistedState || typeof persistedState !== 'object' || Array.isArray(persistedState)) {
          return persistedState;
        }

        const nextState = { ...(persistedState as Record<string, unknown>) };
        delete nextState.douyinImTaskQueue;
        delete nextState.videoPromptOverrides;
        delete nextState.dataSpaceState;
        return nextState;
      },
      partialize: (state) => ({
        theme: state.theme,
        settings: state.settings,
        auth: state.auth,
        currentShopId: state.currentShopId,
        currentEcomAccountId: state.currentEcomAccountId,
        douyinImDebug: state.douyinImDebug,
        displayWorkbench: state.displayWorkbench,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    }
  )
);
